// 🎓 LecturePage Custom Hooks
import { lacksCourseAccess } from '../template/courseOwnershipState';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router';
import { useNavigate } from '../../src/hooks/useI18nNavigate';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

// Import domain-specific React Query hooks and types
import {
  useGetCourseDetail,
  useGetLectureDetail,
  useMarkLectureAsCompleted,
  useGetCourseDraftById,
  getGetCourseDetailQueryKey,
  getGetApiCoursesCourseIdLecturesLectureIdQueryKey,
  CourseDetail,
  CourseDetailSectionsItem
} from '@edusense/course-client';

import { FlatLecture } from './LecturePage.types';

export const useLecturePageData = (propsCourseId?: string, propsLectureId?: string) => {
  // Try to get from React Router first, fallback to props (for Next.js)
  let routerParams: { courseId?: string; lectureId?: string } = {};
  try {
    // This will work in React Router environments
    routerParams = useParams<{ courseId: string; lectureId: string }>() || {};
  } catch (e) {
    // In Next.js environment, useParams won't exist
  }

  const courseId = propsCourseId || routerParams?.courseId;
  const lectureId = propsLectureId || routerParams?.lectureId;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Preview mode 감지
  const isPreviewMode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const previewParam = searchParams.get('preview');
    return previewParam === 'true' || previewParam === '1';
  }, [location.search]);

  // Draft 데이터 가져오기 (Preview mode일 때만)
  const { data: draftData, isLoading: isDraftLoading, error: draftError } = useGetCourseDraftById(
    courseId!,
    {
      query: {
        enabled: isPreviewMode && !!courseId,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        retry: 1,
      },
    },
  );

  // Course 데이터 가져오기 (non-preview mode일 때만)
  const { data: courseData, isLoading: isCourseLoading, error: courseError } = useGetCourseDetail(
    courseId!,
    undefined,
    {
      query: {
        enabled: !isPreviewMode && !!courseId,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        retry: 1,
      },
    },
  );

  // Lecture 세부 정보 가져오기 (non-preview mode일 때만)
  const { data: lectureDetail, isLoading: isLectureLoading, error: lectureError } = useGetLectureDetail(
    courseId!,
    lectureId!,
    undefined, // 3번째 인자: API params (현재 사용하지 않음)
    {          // 4번째 인자: React Query options
      query: {
        enabled: !isPreviewMode && !!courseId && !!lectureId,
        staleTime: 1000 * 60 * 1,
        gcTime: 1000 * 60 * 10,
        retry: 1,
      }
    }
  );

  // Preview mode: Draft에서 lecture 찾기
  const draftLecture = useMemo(() => {
    if (!isPreviewMode || !draftData || !lectureId) {
      console.log('🔍 [Preview] Skipping draft lecture search:', {
        isPreviewMode,
        hasDraftData: !!draftData,
        lectureId
      });
      return null;
    }

    console.log('🔍 [Preview] Searching for lecture in draft:', {
      lectureId,
      sections: draftData.sections?.length,
      totalLectures: draftData.sections?.reduce((sum, s) => sum + (s.lectures?.length || 0), 0)
    });

    // Draft sections에서 lectureId로 lecture 찾기
    for (const section of draftData.sections || []) {
      for (const lecture of section.lectures || []) {
        console.log('🔍 [Preview] Comparing:', {
          lectureId: lecture.id,
          targetId: lectureId,
          match: lecture.id === lectureId,
          lectureTitle: lecture.title
        });

        if (lecture.id === lectureId) {
          console.log('✅ [Preview] Found matching lecture:', lecture);
          return lecture;
        }
      }
    }

    console.error('❌ [Preview] Lecture not found in draft. Available lectures:',
      draftData.sections?.flatMap(s => s.lectures?.map(l => ({ id: l.id, title: l.title })))
    );

    return null;
  }, [isPreviewMode, draftData, lectureId]);

  // Preview mode일 때 draft lecture를 사용, 아니면 API lecture 사용
  const effectiveLectureDetail = isPreviewMode ? draftLecture : lectureDetail;
  const effectiveLectureLoading = isPreviewMode ? isDraftLoading : isLectureLoading;
  const effectiveLectureError = isPreviewMode ? draftError : lectureError;

  // Preview mode일 때는 draft를 courseData로 변환
  const effectiveCourseData = useMemo(() => {
    if (!isPreviewMode) {
      return courseData;
    }

    if (!draftData) {
      return null;
    }

    // Draft를 CourseDetail 형식으로 변환
    return {
      summary: {
        id: draftData.id,
        title: draftData.basicInfo?.title || '',
        description: draftData.basicInfo?.description,
        owned: true, // Draft는 항상 소유자만 볼 수 있음
        instructor: draftData.basicInfo?.instructor,
        thumbnailUrl: draftData.basicInfo?.thumbnailUrl,
        progressRate: 0,
      },
      sections: draftData.sections || [],
      description: draftData.basicInfo?.description,
    } as CourseDetail;
  }, [isPreviewMode, draftData, courseData]);

  // Lecture 완료 처리 Mutation
  const markLectureCompletedMutation = useMarkLectureAsCompleted();

  // --- Effect for checking ownership and handling errors ---
  useEffect(() => {
    // Preview mode에서는 권한 체크 스킵
    if (isPreviewMode) {
      return;
    }

    // courseData 로딩 중이거나, 아직 데이터가 없으면 확인 불가
    if (isCourseLoading || !courseData) {
      return;
    }

    // If there's a lecture error (401 Unauthorized), redirect to course page
    if (lectureError) {
      const errorMessage = (lectureError as any)?.response?.data?.message || '';
      const statusCode = (lectureError as any)?.response?.status;

      // If it's an authorization error, redirect to course page
      if (statusCode === 401 || statusCode === 403 || errorMessage.includes('등록') || errorMessage.includes('enroll')) {
        console.warn(`User not authorized to view lecture. Redirecting to course page.`);
        toast.error('강의를 수강하려면 먼저 등록해주세요.');
        if (courseId) {
          navigate(`/learn/course/${courseId}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        return;
      }
    }

    // courseData.summary.owned가 false이면 강좌 상세 페이지로 리디렉션
    if (lacksCourseAccess(courseData.summary)) {
      console.warn(`User does not own course ${courseId}. Redirecting to course page.`);
      toast.error('강의를 수강하려면 먼저 등록해주세요.');
      if (courseId) {
        navigate(`/learn/course/${courseId}`, { replace: true });
      } else {
        console.error("Cannot redirect: courseId is missing.");
        navigate('/', { replace: true });
      }
    }
  }, [courseData, isCourseLoading, courseError, lectureError, navigate, courseId, isPreviewMode]);

  return {
    courseId,
    lectureId,
    courseData: effectiveCourseData,
    lectureDetail: effectiveLectureDetail,
    isCourseLoading: isPreviewMode ? isDraftLoading : isCourseLoading,
    isLectureLoading: effectiveLectureLoading,
    courseError: isPreviewMode ? draftError : courseError,
    lectureError: effectiveLectureError,
    markLectureCompletedMutation,
    queryClient,
    navigate
  };
};

export const useLectureNavigation = (courseData: CourseDetail | undefined, lectureId: string | undefined) => {
  // --- Memoized 값 (Defensive checks without try-catch) ---
  const curriculum = useMemo(() => courseData?.sections ?? null, [courseData]);

  const flatLectures: FlatLecture[] = useMemo(() => {
    if (!Array.isArray(curriculum)) return [];
    return curriculum.flatMap((section) => {
      if (!section || !Array.isArray(section.lectures)) {
        return [];
      }
      return section.lectures.map(lecture => ({
        id: lecture?.id?.toString() ?? `unknown-${Math.random()}`,
        title: lecture?.title ?? '제목 없음',
        sectionId: section.id?.toString(),
        sectionTitle: section?.title ?? '섹션 제목 없음',
      }));
    });
  }, [curriculum]);

  const currentLectureIndex = useMemo(() => {
    if (!lectureId || !flatLectures || flatLectures.length === 0) return -1;
    return flatLectures.findIndex(lec => lec?.id === lectureId);
  }, [lectureId, flatLectures]);

  const hasPreviousLecture = useMemo(() => currentLectureIndex > 0, [currentLectureIndex]);
  const hasNextLecture = useMemo(() => currentLectureIndex !== -1 && currentLectureIndex < flatLectures.length - 1, [currentLectureIndex, flatLectures]);

  return {
    curriculum,
    flatLectures,
    currentLectureIndex,
    hasPreviousLecture,
    hasNextLecture
  };
};

export const useLectureActions = (
  courseId: string | undefined,
  lectureId: string | undefined,
  canonicalLectureId: string | undefined,
  markLectureCompletedMutation: any,
  queryClient: any,
  navigate: any,
  flatLectures: FlatLecture[],
  currentLectureIndex: number
) => {
  const [showReviewModal, setShowReviewModal] = useState(false);

  const handleGoBack = useCallback(() => {
    if (courseId) {
      navigate(`/learn/course/${courseId}`);
    } else {
      console.error("Cannot go back: courseId is missing.");
      navigate('.');
    }
  }, [navigate, courseId]);

  const handleNavigateLecture = useCallback(async (direction: 'previous' | 'next') => {
    if (currentLectureIndex < 0 || !Array.isArray(flatLectures) || flatLectures.length === 0) return;

    const completionLectureId = canonicalLectureId ?? lectureId;

    // 다음 강의로 이동하는 경우에만 완료 처리
    if (direction === 'next' && completionLectureId) {
      try {
        console.log(`Marking lecture ${completionLectureId} as completed.`);
        await markLectureCompletedMutation.mutateAsync({ lectureId: completionLectureId });

        console.log(`Invalidating queries after marking lecture ${completionLectureId} complete.`);
        if (courseId) {
          // Invalidate all course detail queries (including language-specific ones)
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey as string[];
              return key.some(k => typeof k === 'string' && k.includes(courseId));
            }
          });
        }
        if (courseId && completionLectureId) {
          queryClient.invalidateQueries({
            queryKey: getGetApiCoursesCourseIdLecturesLectureIdQueryKey(courseId, completionLectureId)
          });
        }

      } catch (error) {
        console.error(`Failed to mark lecture ${completionLectureId} as completed:`, error);
        toast.error("강의 완료 처리에 실패했습니다.");
      }
    }

    // 다음/이전 강의로 네비게이션
    const targetIndex = direction === 'previous' ? currentLectureIndex - 1 : currentLectureIndex + 1;
    if (targetIndex >= 0 && targetIndex < flatLectures.length) {
      const targetLecture = flatLectures[targetIndex];
      if (targetLecture?.id && courseId) {
        navigate(`/learn/course/${courseId}/lecture/${targetLecture.id}`);
      } else {
        console.error("Cannot navigate: Invalid target lecture, id, or courseId.", { targetLecture, courseId });
      }
    }
  }, [navigate, courseId, currentLectureIndex, flatLectures, lectureId, canonicalLectureId, markLectureCompletedMutation, queryClient]);

  const handleCompleteCourse = useCallback(async () => {
    const completionLectureId = canonicalLectureId ?? lectureId;

    if (!completionLectureId || !courseId) {
      console.error("Cannot complete course: Missing canonical lectureId or courseId.");
      toast.error("강좌 완료 처리에 필요한 정보가 부족합니다.");
      return;
    }

    try {
      console.log(`Marking final lecture ${completionLectureId} as completed.`);
      await markLectureCompletedMutation.mutateAsync({ lectureId: completionLectureId });

      console.log(`Invalidating course query after completing course.`);
      // Invalidate all course detail queries (including language-specific ones)
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as string[];
          return key.some(k => typeof k === 'string' && k.includes(courseId));
        }
      });
      queryClient.invalidateQueries({
        queryKey: getGetApiCoursesCourseIdLecturesLectureIdQueryKey(courseId, completionLectureId)
      });

      toast.success('강좌 수강을 완료했습니다! 리뷰를 남겨주세요.🎉');
      setShowReviewModal(true);

    } catch (error) {
      console.error(`Failed to mark final lecture ${completionLectureId} as completed:`, error);
      toast.error("강좌 완료 처리에 실패했습니다.");
    }
  }, [canonicalLectureId, lectureId, courseId, markLectureCompletedMutation, queryClient]);

  const handleReviewSubmitSuccess = useCallback(() => {
    console.log('Review submitted successfully.');
    setShowReviewModal(false);
    toast.success('리뷰가 성공적으로 등록되었습니다. 감사합니다!');
    if (courseId) {
      navigate(`/learn/course/${courseId}`);
    }
  }, [courseId, navigate]);

  const handleQuizSubmit = useCallback((answers: Record<string, any>, results: Record<string, 'correct' | 'incorrect' | 'pending'>) => {
    console.log("퀴즈 제출 받음:", { answers, results });
  }, [courseId, lectureId]);

  return {
    showReviewModal,
    setShowReviewModal,
    handleGoBack,
    handleNavigateLecture,
    handleCompleteCourse,
    handleReviewSubmitSuccess,
    handleQuizSubmit
  };
};
